// The pictures an asset can be shown as. Choosing one is an edit of the asset
// (`updateAsset` with `thumbnail_asset_file_id`), not a call here.

import { api } from '../client'
import type { components } from '../schema'
import type { ProcessingJobEntry } from './processing'

export type ThumbnailEntry = components['schemas']['ThumbnailEntry']
export type CaptureThumbnailRequest = components['schemas']['CaptureThumbnailRequest']

/** An asset's stills at its current generation, oldest first, each with a signed URL. */
export function listAssetThumbnails(assetId: string, signal?: AbortSignal): Promise<ThumbnailEntry[]> {
  return api.get<ThumbnailEntry[]>(`/assets/${encodeURIComponent(assetId)}/thumbnails`, undefined, signal)
}

/** Add a picture of one's own. It is not selected. */
export function uploadAssetThumbnail(assetId: string, file: File): Promise<ThumbnailEntry> {
  const form = new FormData()
  form.append('file', file)
  return api.postForm<ThumbnailEntry>(`/assets/${encodeURIComponent(assetId)}/thumbnails`, form)
}

/**
 * Ask for a frame of the video, at an instant of your choosing. **Nothing is returned but the
 * job**: the answer is a `202` and the still lands later, so the caller follows
 * `getAssetProcessing` for this `job_id` and reads the list again once it is over. Asking twice at
 * the same instant answers the same job. `422` for an asset that is not a video, an instant past
 * the end, or bytes that have not landed; `429` at the organization's queue ceiling.
 */
export function captureAssetThumbnail(assetId: string, atMs: number): Promise<ProcessingJobEntry> {
  const request: CaptureThumbnailRequest = { at_ms: atMs }
  return api.post<ProcessingJobEntry>(`/assets/${encodeURIComponent(assetId)}/thumbnails/captures`, request)
}

/** Take a picture away. Where the asset had it selected, the selection is cleared with it. */
export function deleteAssetThumbnail(assetId: string, thumbnailId: string): Promise<void> {
  return api.del<void>(`/assets/${encodeURIComponent(assetId)}/thumbnails/${encodeURIComponent(thumbnailId)}`)
}
